import { ConnectionStatus, } from "@electrum-cash/network";
import { Network } from "../interface.js";
import { getTransactionHash } from "../util/transaction.js";
import { Config } from "../config.js";
import { decodeHeader } from "../util/header.js";
import { IndexedDbCache } from "../cache/IndexedDbCache.js";
import { WebStorageCache } from "../cache/WebStorageCache.js";
import { MemoryCache } from "../cache/MemoryCache.js";
export default class ElectrumNetworkProvider {
    get cache() {
        if (!Config.UseMemoryCache &&
            !Config.UseLocalStorageCache &&
            !Config.UseIndexedDBCache) {
            this._cache = undefined;
            return this._cache;
        }
        if (Config.UseMemoryCache && !(this._cache instanceof MemoryCache)) {
            this._cache = new MemoryCache();
            return this._cache;
        }
        if (Config.UseLocalStorageCache &&
            !(this._cache instanceof WebStorageCache)) {
            this._cache = new WebStorageCache();
            return this._cache;
        }
        if (Config.UseIndexedDBCache && !(this._cache instanceof IndexedDbCache)) {
            this._cache = new IndexedDbCache();
            return this._cache;
        }
        return this._cache;
    }
    constructor(electrum, network = Network.MAINNET, manualConnectionManagement) {
        this.network = network;
        this.manualConnectionManagement = manualConnectionManagement;
        this.subscriptions = 0;
        this.subscriptionMap = {};
        this.currentHeight = 0;
        if (electrum) {
            this.electrum = electrum;
        }
        else {
            throw new Error(`A electrum-cash client is required.`);
        }
    }
    async getUtxos(cashaddr) {
        const result = await this.performRequest("blockchain.address.listunspent", cashaddr, "include_tokens");
        return result.map((utxo) => ({
            address: cashaddr,
            txid: utxo.tx_hash,
            vout: utxo.tx_pos,
            satoshis: BigInt(utxo.value),
            height: utxo.height,
            token: utxo.token_data
                ? {
                    ...utxo.token_data,
                    amount: BigInt(utxo.token_data.amount),
                }
                : undefined,
        }));
    }
    async getBalance(cashaddr) {
        const result = await this.performRequest("blockchain.address.get_balance", cashaddr);
        return BigInt(result.confirmed) + BigInt(result.unconfirmed);
    }
    async getHeader(height, verbose = false) {
        const key = `header-${this.network}-${height}-${verbose}`;
        if (this.cache) {
            const cached = await this.cache.getItem(key);
            if (cached) {
                return verbose ? decodeHeader(JSON.parse(cached)) : JSON.parse(cached);
            }
        }
        const result = await this.performRequest("blockchain.header.get", height);
        if (this.cache) {
            await this.cache.setItem(key, JSON.stringify(result));
        }
        return verbose ? decodeHeader(result) : result;
    }
    async getRawTransactions(hashes) {
        if (hashes.length === 0)
            return new Map();
        const results = new Map();
        const keys = hashes.map((hash) => `txraw-${this.network}-${hash}`);
        // batch cache read
        let cached;
        if (this.cache) {
            cached = await this.cache.getItems(keys);
        }
        const misses = [];
        for (let i = 0; i < hashes.length; i++) {
            const val = cached?.get(keys[i]);
            if (val) {
                results.set(hashes[i], val);
            }
            else {
                misses.push(hashes[i]);
            }
        }
        if (misses.length > 0) {
            const fetched = await Promise.all(misses.map(async (hash) => {
                const tx = await this.performRequest("blockchain.transaction.get", hash, false);
                return [hash, tx];
            }));
            // batch cache write
            if (this.cache) {
                const entries = fetched.map(([hash, tx]) => [
                    `txraw-${this.network}-${hash}`,
                    tx,
                ]);
                await this.cache.setItems(entries);
            }
            for (const [hash, tx] of fetched) {
                results.set(hash, tx);
            }
        }
        return results;
    }
    async getHeaders(heights) {
        if (heights.length === 0)
            return new Map();
        const results = new Map();
        const keys = heights.map((height) => `header-${this.network}-${height}-true`);
        // batch cache read
        let cached;
        if (this.cache) {
            cached = await this.cache.getItems(keys);
        }
        const misses = [];
        for (let i = 0; i < heights.length; i++) {
            const val = cached?.get(keys[i]);
            if (val) {
                results.set(heights[i], decodeHeader(JSON.parse(val)));
            }
            else {
                misses.push(heights[i]);
            }
        }
        if (misses.length > 0) {
            const fetched = await Promise.all(misses.map(async (height) => {
                const result = await this.performRequest("blockchain.header.get", height);
                return [height, result];
            }));
            // batch cache write
            if (this.cache) {
                const entries = fetched.map(([height, result]) => [
                    `header-${this.network}-${height}-true`,
                    JSON.stringify(result),
                ]);
                await this.cache.setItems(entries);
            }
            for (const [height, result] of fetched) {
                results.set(height, decodeHeader(result));
            }
        }
        return results;
    }
    async getBlockHeight() {
        if (!this.headerCancelFn) {
            this.headerCancelFn = await this.subscribeToHeaders((header) => {
                if (header.height > this.currentHeight) {
                    this.currentHeight = header.height;
                }
            });
        }
        if (!this.currentHeight) {
            throw new Error("Check failed for eventual inconsistency in subscription implementations.");
        }
        return this.currentHeight;
    }
    async getRawTransaction(txHash, verbose = false, loadInputValues = false) {
        const nonVerboseKey = `txraw-${this.network}-${txHash}`;
        const verboseKey = `tx-${this.network}-${txHash}`;
        const key = verbose ? verboseKey : nonVerboseKey;
        if (this.cache) {
            const cached = await this.cache.getItem(key);
            if (cached) {
                if (!verbose) {
                    return cached;
                }
                const cachedTx = JSON.parse(cached);
                if (cachedTx.confirmations > 0) {
                    const currentHeight = await this.getBlockHeight();
                    cachedTx.confirmations += currentHeight - cachedTx.fetchHeight;
                }
                const { fetchHeight: _, ...transaction } = cachedTx;
                if (loadInputValues) {
                    return this.enrichWithInputValues(transaction);
                }
                return transaction;
            }
        }
        try {
            const result = await this.performRequest("blockchain.transaction.get", txHash, verbose);
            if (!verbose) {
                const hex = result;
                if (this.cache) {
                    await this.cache.setItem(key, hex);
                }
                return hex;
            }
            const cachedTx = result;
            cachedTx.confirmations ??= 0;
            cachedTx.fetchHeight = await this.getBlockHeight();
            if (this.cache) {
                await this.cache.setItem(key, JSON.stringify(cachedTx));
            }
            const { fetchHeight: _, ...transaction } = cachedTx;
            if (loadInputValues) {
                return this.enrichWithInputValues(transaction);
            }
            return transaction;
        }
        catch (error) {
            if (error.message.indexOf("No such mempool or blockchain transaction.") > -1)
                throw Error(`Could not decode transaction ${txHash}. It might not exist on the current blockchain (${this.network}).`);
            else
                throw error;
        }
    }
    async enrichWithInputValues(transaction) {
        const hashes = [...new Set(transaction.vin.map((val) => val.txid))];
        const transactions = await Promise.all(hashes.map((hash) => this.getRawTransactionObject(hash, false)));
        const transactionMap = new Map();
        transactions.forEach((val) => transactionMap.set(val.hash, val));
        const enrichedVin = transaction.vin.map((input) => {
            const output = transactionMap
                .get(input.txid)
                .vout.find((val) => val.n === input.vout);
            return { ...input, ...output };
        });
        return { ...transaction, vin: enrichedVin };
    }
    async getRawTransactionObject(txHash, loadInputValues = false) {
        if (loadInputValues) {
            return this.getRawTransaction(txHash, true, true);
        }
        return this.getRawTransaction(txHash, true);
    }
    async sendRawTransaction(txHex, awaitPropagation = true) {
        return new Promise(async (resolve, reject) => {
            let txHash = await getTransactionHash(txHex);
            if (!awaitPropagation) {
                this.performRequest("blockchain.transaction.broadcast", txHex);
                resolve(txHash);
            }
            else {
                let cancel;
                const waitForTransactionCallback = async (data) => {
                    if (data && data[0] === txHash && data[1] !== null) {
                        await cancel?.();
                        resolve(txHash);
                    }
                };
                cancel = await this.subscribeToTransaction(txHash, waitForTransactionCallback);
                this.performRequest("blockchain.transaction.broadcast", txHex).catch(async (error) => {
                    await cancel?.();
                    reject(error);
                });
            }
        });
    }
    // Get transaction history of a given cashaddr
    async getHistory(cashaddr, fromHeight = 0, toHeight = -1) {
        const result = await this.performRequest("blockchain.address.get_history", cashaddr, fromHeight, toHeight);
        return result;
    }
    // Get the minimum fee a low-priority transaction must pay in order to be accepted to the daemon's memory pool.
    async getRelayFee() {
        const result = (await this.performRequest("blockchain.relayfee"));
        return result;
    }
    async watchAddressStatus(cashaddr, callback) {
        const watchAddressStatusCallback = async (data) => {
            // subscription acknowledgement is the latest known status or null if no status is known
            // status is an array: [ cashaddr, statusHash ]
            if (data instanceof Array) {
                if (data[0] !== cashaddr) {
                    return;
                }
                callback(data[1]);
            }
        };
        return this.subscribeToAddress(cashaddr, watchAddressStatusCallback);
    }
    // watch for block headers and block height, if `skipCurrentHeight` is set, the notification about current block will not arrive
    async watchBlocks(callback, skipCurrentHeight = true) {
        let acknowledged = !skipCurrentHeight;
        const waitForBlockCallback = (_header) => {
            if (!acknowledged) {
                acknowledged = true;
                return;
            }
            _header = _header instanceof Array ? _header[0] : _header;
            callback(_header);
        };
        return this.subscribeToHeaders(waitForBlockCallback);
    }
    // Wait for the next block or a block at given blockchain height.
    async waitForBlock(height) {
        return new Promise(async (resolve) => {
            let cancelWatch;
            if (this.electrum.chainHeight && !height) {
                height = this.electrum.chainHeight + 1;
            }
            cancelWatch = await this.watchBlocks(async (header) => {
                if (!height) {
                    height = header.height + 1;
                    return;
                }
                if (header.height >= height) {
                    await cancelWatch?.();
                    resolve(header);
                    return;
                }
            });
        });
    }
    // subscribe to notifications sent when new block is found, the block header is sent to callback
    async subscribeToHeaders(callback) {
        return this.subscribeRequest("blockchain.headers.subscribe", (data) => {
            callback(data[0] ?? data);
        });
    }
    async subscribeToAddress(cashaddr, callback) {
        return this.subscribeRequest("blockchain.address.subscribe", callback, cashaddr);
    }
    async subscribeToTransaction(txHash, callback) {
        return this.subscribeRequest("blockchain.transaction.subscribe", callback, txHash);
    }
    async performRequest(name, ...parameters) {
        await this.ready();
        const requestTimeout = new Promise(function (_resolve, reject) {
            setTimeout(function () {
                reject("electrum-cash request timed out, retrying");
            }, 30000);
        }).catch(function (e) {
            throw e;
        });
        const request = this.electrum.request(name, ...parameters);
        return await Promise.race([request, requestTimeout])
            .then((value) => {
            if (value instanceof Error)
                throw value;
            let result = value;
            return result;
        })
            .catch(async () => {
            return await Promise.race([request, requestTimeout])
                .then((value) => {
                if (value instanceof Error)
                    throw value;
                let result = value;
                return result;
            })
                .catch(function (e) {
                throw e;
            });
        });
    }
    async trackSubscription(methodName, ...parameters) {
        const key = `${methodName}-${this.network}-${JSON.stringify(parameters)}`;
        if (this.subscriptionMap[key]) {
            this.subscriptionMap[key]++;
        }
        else {
            this.subscriptionMap[key] = 1;
        }
        await this.electrum.subscribe(methodName, ...parameters);
    }
    async untrackSubscription(methodName, ...parameters) {
        const key = `${methodName}-${this.network}-${JSON.stringify(parameters)}`;
        if (this.subscriptionMap[key]) {
            this.subscriptionMap[key]--;
            if (this.subscriptionMap[key] <= 0) {
                // only really unsubscribe if there are no more subscriptions for this `key`
                delete this.subscriptionMap[key];
                try {
                    await this.electrum.unsubscribe(methodName, ...parameters);
                }
                catch { }
            }
        }
    }
    async subscribeRequest(methodName, callback, ...parameters) {
        await this.ready();
        const handler = (data) => {
            if (data.method === methodName) {
                callback(data.params);
            }
        };
        this.electrum.on("notification", handler);
        await this.trackSubscription(methodName, ...parameters);
        this.subscriptions++;
        return async () => {
            this.electrum.off("notification", handler);
            await this.untrackSubscription(methodName, ...parameters);
            this.subscriptions--;
        };
    }
    async ready() {
        return this.connect();
    }
    async connect() {
        await this.cache?.init();
        if (this.electrum.status !== ConnectionStatus.CONNECTED) {
            await this.electrum.connect();
        }
    }
    async disconnect() {
        if (this.subscriptions > 0) {
            // console.warn(
            //   `Trying to disconnect a network provider with ${this.subscriptions} active subscriptions. This is in most cases a bad idea.`
            // );
        }
        await this.headerCancelFn?.();
        return this.electrum.disconnect(true, false);
    }
}
//# sourceMappingURL=ElectrumNetworkProvider.js.map